import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Offline-first action queue for the field PWAs (Tug Captain, Pilot) — NFR-2.
 *
 * Actions are sent immediately when online; when offline (or a send fails) they
 * are persisted to localStorage and replayed in order on reconnect, so clock /
 * SoF / pilot-status events are never lost on a flaky handheld connection.
 *
 * Usage:
 *   const send = useCallback((item) => api.something(item), []); // MUST be memoised
 *   const { syncStatus, enqueueOrSend, pendingCount } = useOfflineQueue('key', send);
 *
 * enqueueOrSend resolves to { rejected: err } when the server refuses the action
 * (a 4xx validation error) — it is not queued, so the caller should undo any
 * optimistic state and show err.message.
 *
 * syncStatus ∈'Sync OK' | 'Offline' | 'Offline — queued' | 'Syncing…' |
 *              'Sync Success' | 'Sync Error'.
 */
/** A 4xx the server will give again on every retry (not auth, timeout or rate limit). */
function isRejection(err) {
  return err?.status >= 400 && err.status < 500 && ![401, 403, 408, 429].includes(err.status);
}

export default function useOfflineQueue(queueKey, sendFn) {
  const [syncStatus, setSyncStatus] = useState('Sync OK');
  const queueRef = useRef([]);

  const persist = useCallback(() => {
    localStorage.setItem(queueKey, JSON.stringify(queueRef.current));
  }, [queueKey]);

  const flushQueue = useCallback(async () => {
    if (queueRef.current.length === 0) {
      setSyncStatus('Sync OK');
      return;
    }
    setSyncStatus('Syncing…');
    const remaining = [];
    for (const item of queueRef.current) {
      try {
        await sendFn(item);
      } catch (err) {
        // The server refused the action itself; replaying it will never succeed.
        if (!isRejection(err)) remaining.push(item);
      }
    }
    queueRef.current = remaining;
    persist();
    setSyncStatus(remaining.length === 0 ? 'Sync Success' : 'Sync Error');
  }, [sendFn, persist]);

  const enqueueOrSend = useCallback(
    async (item) => {
      if (!navigator.onLine) {
        queueRef.current.push(item);
        persist();
        setSyncStatus('Offline — queued');
        return;
      }
      try {
        await sendFn(item);
      } catch (err) {
        // A validation refusal is the caller's to show, not a sync failure to retry.
        if (isRejection(err)) return { rejected: err };
        queueRef.current.push(item);
        persist();
        setSyncStatus('Sync Error');
      }
      return undefined;
    },
    [sendFn, persist]
  );

  // Restore any queued actions, do an initial flush, and watch connectivity.
  useEffect(() => {
    try {
      queueRef.current = JSON.parse(localStorage.getItem(queueKey) || '[]');
    } catch {
      queueRef.current = [];
    }
    if (!navigator.onLine) setSyncStatus('Offline');
    else if (queueRef.current.length > 0) flushQueue();

    const goOffline = () => setSyncStatus('Offline');
    const goOnline = () => flushQueue();
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, [queueKey, flushQueue]);

  const pendingCount = useCallback(() => queueRef.current.length, []);

  return { syncStatus, setSyncStatus, enqueueOrSend, flushQueue, pendingCount };
}
